import { observable, action, makeObservable } from "mobx";
import ApiBasedStore from "./ApiBasedStore";

export default class AllDeeplinksStore extends ApiBasedStore {
  deeplinks = [];
  totalDeeplinks = 0;
  currentPage = 1;
  pageSize = 20;
  searchQuery = "";
  creatingShortUrls = new Set();
  loadingUtmConfigs = new Set();
  savingUtmConfigs = new Set();
  deletingUtmConfigs = new Set();
  creatingBulkUtm = false;
  deletingBulkUtm = false;

  constructor(rootStore, api) {
    super(rootStore);
    this.api = api;
    makeObservable(this, {
      deeplinks: observable,
      totalDeeplinks: observable,
      currentPage: observable,
      pageSize: observable,
      searchQuery: observable,
      creatingShortUrls: observable,
      loadingUtmConfigs: observable,
      savingUtmConfigs: observable,
      deletingUtmConfigs: observable,
      creatingBulkUtm: observable,
      deletingBulkUtm: observable,
      fetchDeeplinks: action,
      createShortUrl: action,
      setCurrentPage: action,
      setSearchQuery: action,
      createUtmConfig: action,
      deleteUtmConfig: action,
      fetchUtmConfigs: action,
      createBulkUtmConfig: action,
      deleteBulkUtmConfig: action,
    });
  }

  async fetchDeeplinks(page = 1, pageSize = this.pageSize, search = this.searchQuery) {
    try {
      this.startRequest();
      const pageIndex = page - 1; // Convert to 0-based index
      const response = await this.api.getAllDeeplinks(pageSize, pageIndex, search);

      // The API now returns deeplinks with utmConfigurations included
      this.deeplinks = response.deeplinks || [];
      this.totalDeeplinks = response.total || 0;
      this.currentPage = page;

      this.endRequest();
      return response;
    } catch (error) {
      this.onError(error);
      throw error;
    }
  }

  async createShortUrl(url, path) {
    try {
      this.creatingShortUrls.add(path);
      const response = await this.api.createShortUrl(url);

      // Update the deeplink with the new short URL
      const deeplink = this.deeplinks.find((d) => d.path === path);
      if (deeplink) {
        deeplink.shortUrl = response.shortUrl;
      }

      this.creatingShortUrls.delete(path);
      return response;
    } catch (error) {
      this.creatingShortUrls.delete(path);
      throw error;
    }
  }

  setCurrentPage(page) {
    this.currentPage = page;
    this.fetchDeeplinks(page);
  }

  setSearchQuery(query) {
    this.searchQuery = query;
    this.currentPage = 1; // Reset to first page when searching
    this.fetchDeeplinks(1, this.pageSize, query);
  }

  get totalPages() {
    return Math.ceil(this.totalDeeplinks / this.pageSize);
  }

  async createUtmConfig(path, config) {
    try {
      this.savingUtmConfigs.add(path);
      const response = await this.api.createUtmConfig(path, config);

      // Update local state directly with the returned config
      const deeplink = this.deeplinks.find((d) => d.path === path);
      if (deeplink) {
        if (!deeplink.utmConfigurations) {
          deeplink.utmConfigurations = [];
        }
        // Add the newly created config to the array (backend now returns url field)
        deeplink.utmConfigurations.push({
          id: response.id,
          utmSource: response.utmSource,
          utmMedium: response.utmMedium,
          name: response.name,
          url: response.url  // Backend now includes this field
        });
      }

      this.savingUtmConfigs.delete(path);
      return response;
    } catch (error) {
      this.savingUtmConfigs.delete(path);
      throw error;
    }
  }

  async fetchUtmConfigs(path) {
    try {
      this.loadingUtmConfigs.add(path);
      const response = await this.api.getUtmConfigs(path);

      const deeplink = this.deeplinks.find((d) => d.path === path);
      if (deeplink) {
        // Backend returns { configurations: [...] }, extract the array
        deeplink.utmConfigurations = response.configurations || response;
      }

      this.loadingUtmConfigs.delete(path);
      return response;
    } catch (error) {
      this.loadingUtmConfigs.delete(path);
      throw error;
    }
  }

  async deleteUtmConfig(path, configId) {
    try {
      const key = `${path}-${configId}`;
      this.deletingUtmConfigs.add(key);
      await this.api.deleteUtmConfig(path, configId);

      // Remove from local state - find config by the actual ID
      const deeplink = this.deeplinks.find((d) => d.path === path);
      if (deeplink && deeplink.utmConfigurations) {
        deeplink.utmConfigurations = deeplink.utmConfigurations.filter((config) => config.id !== configId);
      }

      this.deletingUtmConfigs.delete(key);
    } catch (error) {
      const key = `${path}-${configId}`;
      this.deletingUtmConfigs.delete(key);
      throw error;
    }
  }

  async createBulkUtmConfig(config) {
    try {
      this.creatingBulkUtm = true;
      const response = await this.api.createBulkUtmConfig(config);

      // Refresh all deeplinks to get updated UTM configurations
      await this.fetchDeeplinks(this.currentPage, this.pageSize, this.searchQuery);

      this.creatingBulkUtm = false;
      return response;
    } catch (error) {
      this.creatingBulkUtm = false;
      throw error;
    }
  }

  async deleteBulkUtmConfig(config) {
    try {
      this.deletingBulkUtm = true;
      const response = await this.api.deleteBulkUtmConfig(config);

      // Update local state immediately - remove matching configs from all deeplinks
      this.deeplinks.forEach(deeplink => {
        if (deeplink.utmConfigurations) {
          deeplink.utmConfigurations = deeplink.utmConfigurations.filter(utmConfig => {
            const sourceMatches = utmConfig.utmSource === config.utmSource;
            const mediumMatches = config.utmMedium 
              ? utmConfig.utmMedium === config.utmMedium
              : !utmConfig.utmMedium; // If no medium specified, match configs without medium
            return !(sourceMatches && mediumMatches);
          });
        }
      });

      this.deletingBulkUtm = false;
      return response;
    } catch (error) {
      this.deletingBulkUtm = false;
      throw error;
    }
  }
}
